import * as crypto from "node:crypto";

import { CliError, EXIT_INPUT } from "../../common.js";
import { canonicalNameKey } from "../script/names.js";
import { V4_ID_ALPHABET, V4_ID_PREFIXES, type V4IdKind } from "../script/v4/ids.js";
import type {
  ActionV4,
  ActorRefV4,
  ActorV4,
  EpisodeV4,
  ExtensionMap,
  LocationRefV4,
  LocationV4,
  PropRefV4,
  PropV4,
  SceneV4,
  ScriptV4,
  StateIdV4,
  StateV4,
} from "../script/v4/types.js";
import { validateScriptV4 } from "../script/v4/validation.js";

type RecordValue = Record<string, unknown>;
type AssetKind = "actors" | "locations" | "props";

export interface CreativeDraftComposeResult {
  script: ScriptV4;
  validation: ReturnType<typeof validateScriptV4>;
  episodeNumber: number;
  sceneNumber: number;
  sceneId: string;
  actionIds: string[];
  joinedPreviousScene: boolean;
  created: {
    actors: string[];
    locations: string[];
    props: string[];
    states: string[];
  };
}

function invalid(message: string, actual: unknown[] = []): never {
  throw new CliError(message, {
    exitCode: EXIT_INPUT,
    errorCode: "CREATIVE_DRAFT_INVALID",
    hint: "Use the readable creative draft schema; do not include Script IDs or .scriptctl syntax.",
    data: { actual: actual.map(String) },
  });
}

function isRecord(value: unknown): value is RecordValue {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function clean(value: unknown, fallback = ""): string {
  return String(value ?? "").replace(/\s+/gu, " ").trim() || fallback;
}

function positiveInteger(value: unknown, label: string): number {
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 1) invalid(`${label} must be a positive integer.`, [value]);
  return parsed;
}

function extensionMap(value: unknown): ExtensionMap {
  if (value === undefined) return {};
  if (!isRecord(value)) invalid("Action extensions must be an object.");
  return structuredClone(value) as ExtensionMap;
}

function existingIds(script: ScriptV4): Set<string> {
  const ids = new Set<string>();
  for (const actor of script.actors) {
    ids.add(actor.actor_id);
    for (const state of actor.states) ids.add(state.state_id);
  }
  for (const location of script.locations) {
    ids.add(location.location_id);
    for (const state of location.states) ids.add(state.state_id);
  }
  for (const prop of script.props) {
    ids.add(prop.prop_id);
    for (const state of prop.states) ids.add(state.state_id);
  }
  for (const episode of script.episodes) {
    ids.add(episode.episode_id);
    for (const scene of episode.scenes) {
      ids.add(scene.scene_id);
      for (const action of scene.actions) ids.add(action.action_id);
    }
  }
  return ids;
}

function stableId(kind: V4IdKind, seed: string, used: Set<string>): string {
  for (let nonce = 0; nonce < 128; nonce += 1) {
    const bytes = crypto.createHash("sha256").update(`${kind}:${seed}:${nonce}`).digest();
    let suffix = "";
    for (let index = 0; index < 6; index += 1) {
      suffix += V4_ID_ALPHABET[bytes[index]! % V4_ID_ALPHABET.length];
    }
    const candidate = `${V4_ID_PREFIXES[kind]}${suffix}`;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
  }
  invalid(`Could not allocate a stable ${kind} ID.`);
}

function timestampMs(value: unknown): number | null {
  const match = clean(value).match(/^(\d+):([0-5]\d)(?:\.(\d{1,3}))?$/u);
  if (!match) return null;
  return (Number(match[1]) * 60 + Number(match[2])) * 1_000
    + Number((match[3] ?? "").padEnd(3, "0"));
}

function formatTimestamp(value: number): string {
  return `${Math.floor(value / 60_000)}:${String(Math.floor((value % 60_000) / 1_000)).padStart(2, "0")}.${String(value % 1_000).padStart(3, "0")}`;
}

function actionDuration(action: ActionV4): number {
  const explicit = Number(action.extend?.["duration_ms"] ?? action.extend?.["rewrite_duration_ms"]);
  return Number.isFinite(explicit) && explicit > 0 ? Math.round(explicit) : 1_200;
}

function episodeCursor(episode: EpisodeV4): number {
  let cursor = 0;
  for (const action of episode.scenes.flatMap((scene) => scene.actions)) {
    const explicitStart = timestampMs(action.extend?.["timestamp"]);
    cursor = (explicitStart ?? cursor) + actionDuration(action);
  }
  return cursor;
}

function readableVariants(value: unknown): RecordValue[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || !value.every(isRecord)) invalid("Asset variants must be an array of readable objects.");
  return value;
}

function declaredAssets(draft: RecordValue, kind: AssetKind): RecordValue[] {
  const assets = draft["assets"];
  if (assets === undefined) return [];
  if (!isRecord(assets)) invalid("assets must be an object.");
  const values = assets[kind];
  if (values === undefined) return [];
  if (!Array.isArray(values) || !values.every(isRecord)) invalid(`assets.${kind} must be an array.`);
  return values;
}

function assetName(kind: AssetKind, value: ActorV4 | LocationV4 | PropV4): string {
  if (kind === "actors") return (value as ActorV4).actor_name;
  if (kind === "locations") return (value as LocationV4).location_name;
  return (value as PropV4).prop_name;
}

function assetId(kind: AssetKind, value: ActorV4 | LocationV4 | PropV4): string {
  if (kind === "actors") return (value as ActorV4).actor_id;
  if (kind === "locations") return (value as LocationV4).location_id;
  return (value as PropV4).prop_id;
}

function assetList(script: ScriptV4, kind: AssetKind): Array<ActorV4 | LocationV4 | PropV4> {
  return script[kind] as Array<ActorV4 | LocationV4 | PropV4>;
}

function nameForms(kind: AssetKind, value: ActorV4 | LocationV4 | PropV4): string[] {
  return [assetName(kind, value), ...(value.aliases ?? [])].map(canonicalNameKey);
}

function resolveDeclaration(draft: RecordValue, kind: AssetKind, name: string): RecordValue | null {
  const key = canonicalNameKey(name);
  return declaredAssets(draft, kind).find((entry) => (
    [entry["name"], ...(Array.isArray(entry["aliases"]) ? entry["aliases"] : [])]
      .map((value) => canonicalNameKey(clean(value)))
      .includes(key)
  )) ?? null;
}

function declaredVariant(declaration: RecordValue | null, variant: string): RecordValue | null {
  if (!declaration) return null;
  const key = canonicalNameKey(variant);
  return readableVariants(declaration["variants"]).find((item) => {
    const name = canonicalNameKey(clean(item["name"]));
    return name === key || (["default", "默认"].includes(key) && ["default", "默认"].includes(name));
  }) ?? null;
}

function describedVariant(declaration: RecordValue | null, ownerName: string, variant: string): RecordValue {
  const value = declaredVariant(declaration, variant);
  const description = clean(value?.["description"]);
  if (!value || !description) {
    invalid(`${ownerName}/${variant} is a new variant and requires a readable assets declaration with a self-contained description.`);
  }
  return value;
}

function baseStateName(kind: AssetKind, variant: RecordValue | null): string {
  const name = clean(variant?.["name"]);
  if (name && !["default", "默认"].includes(canonicalNameKey(name))) return name;
  if (kind === "actors") return "基础造型";
  if (kind === "locations") return "常规布置";
  return "基础外观";
}

function createAsset(
  script: ScriptV4,
  draft: RecordValue,
  kind: AssetKind,
  name: string,
  used: Set<string>,
  created: CreativeDraftComposeResult["created"],
): ActorV4 | LocationV4 | PropV4 {
  const declaration = resolveDeclaration(draft, kind, name);
  if (!declaration) invalid(`${name} is not an existing ${kind} asset and has no readable declaration.`);
  const canonicalName = clean(declaration["name"], name);
  const variants = readableVariants(declaration["variants"]);
  const defaultVariant = declaredVariant(declaration, "默认") ?? variants[0] ?? null;
  if (kind === "actors" && !clean(declaration["description"])) {
    invalid(`${canonicalName} requires an identity description separate from its appearance variants.`);
  }
  if (kind === "actors" && !clean(defaultVariant?.["description"])) {
    invalid(`${canonicalName} requires its first variant to provide a self-contained base appearance description.`);
  }
  const states: StateV4[] = [{
    state_id: "default",
    state_name: baseStateName(kind, defaultVariant),
    description: clean(
      defaultVariant?.["description"],
      clean(declaration["description"], `${canonicalName}的基础参考外观。`),
    ),
  }];
  for (const variant of variants) {
    const variantName = clean(variant["name"]);
    if (variant === defaultVariant || !variantName || ["default", "默认"].includes(canonicalNameKey(variantName))) continue;
    const stateId = stableId("state", `${kind}:${canonicalName}:${variantName}`, used) as StateIdV4;
    states.push({
      state_id: stateId,
      state_name: variantName,
      description: clean(describedVariant(declaration, canonicalName, variantName)["description"]),
    });
    created.states.push(`${canonicalName}/${variantName}`);
  }
  const common = {
    aliases: Array.isArray(declaration["aliases"])
      ? [...new Set(declaration["aliases"].map((alias) => clean(alias)).filter((alias) => alias && alias !== canonicalName))]
      : [],
    importance: declaration["importance"] === "background" ? "background" as const : "featured" as const,
    description: clean(declaration["description"], `${canonicalName}的基础设定。`),
    states,
  };
  if (kind === "actors") {
    const value: ActorV4 = {
      ...common,
      actor_id: stableId("actor", canonicalName, used) as ActorV4["actor_id"],
      actor_name: canonicalName,
      ...(declaration["roleType"] === "主角" || declaration["roleType"] === "配角"
        ? { role_type: declaration["roleType"] }
        : {}),
    };
    script.actors.push(value);
    created.actors.push(canonicalName);
    return value;
  }
  if (kind === "locations") {
    const value: LocationV4 = {
      ...common,
      location_id: stableId("location", canonicalName, used) as LocationV4["location_id"],
      location_name: canonicalName,
    };
    script.locations.push(value);
    created.locations.push(canonicalName);
    return value;
  }
  const value: PropV4 = {
    ...common,
    prop_id: stableId("prop", canonicalName, used) as PropV4["prop_id"],
    prop_name: canonicalName,
  };
  script.props.push(value);
  created.props.push(canonicalName);
  return value;
}

function resolveAsset(
  script: ScriptV4,
  draft: RecordValue,
  kind: AssetKind,
  nameValue: unknown,
  used: Set<string>,
  created: CreativeDraftComposeResult["created"],
): ActorV4 | LocationV4 | PropV4 {
  const name = clean(nameValue);
  if (!name) invalid(`${kind} reference requires a readable name.`);
  const key = canonicalNameKey(name);
  const existing = assetList(script, kind).find((candidate) => nameForms(kind, candidate).includes(key));
  return existing ?? createAsset(script, draft, kind, name, used, created);
}

function resolveState(
  owner: ActorV4 | LocationV4 | PropV4,
  variantValue: unknown,
  draft: RecordValue,
  kind: AssetKind,
  used: Set<string>,
  created: CreativeDraftComposeResult["created"],
): StateIdV4 {
  const variant = clean(variantValue, "默认");
  const key = canonicalNameKey(variant);
  const existing = owner.states.find((state) => (
    canonicalNameKey(state.state_name) === key
    || (state.state_id === "default" && ["default", "默认"].includes(key))
  ));
  if (existing) return existing.state_id;
  const ownerName = "actor_name" in owner ? owner.actor_name : "location_name" in owner ? owner.location_name : owner.prop_name;
  const declaration = resolveDeclaration(draft, kind, ownerName);
  const variantDeclaration = describedVariant(declaration, ownerName, variant);
  const state: StateV4 = {
    state_id: stableId("state", `${ownerName}:${variant}`, used) as StateIdV4,
    state_name: variant,
    description: clean(variantDeclaration["description"]),
  };
  owner.states.push(state);
  created.states.push(`${ownerName}/${variant}`);
  return state.state_id;
}

function readableRefs(value: unknown, label: string): RecordValue[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || !value.every(isRecord)) invalid(`${label} must be an array of readable references.`);
  return value;
}

function mergeStateRefs<T extends { state_ids: StateIdV4[] }>(
  current: T[],
  incoming: T[],
  idOf: (value: T) => string,
): T[] {
  const result = structuredClone(current);
  for (const ref of incoming) {
    const existing = result.find((candidate) => idOf(candidate) === idOf(ref));
    if (!existing) result.push(structuredClone(ref));
    else existing.state_ids = [...new Set([...existing.state_ids, ...ref.state_ids])].sort();
  }
  return result;
}

function sameSetup(previous: SceneV4 | undefined, incoming: SceneV4): boolean {
  return Boolean(
    previous
    && previous.location?.location_id
    && incoming.location?.location_id
    && previous.location.location_id === incoming.location.location_id
    && clean(previous.environment?.space) === clean(incoming.environment?.space)
    && clean(previous.environment?.time) === clean(incoming.environment?.time),
  );
}

function draftActions(
  value: unknown,
  script: ScriptV4,
  draft: RecordValue,
  actors: ActorRefV4[],
  sceneSeed: string,
  cursorStart: number,
  used: Set<string>,
  created: CreativeDraftComposeResult["created"],
): { actions: ActionV4[]; actorRefs: ActorRefV4[] } {
  if (!Array.isArray(value) || value.length === 0 || !value.every(isRecord)) {
    invalid("scene.actions must contain at least one readable Action.");
  }
  const actorRefs = structuredClone(actors);
  let cursor = cursorStart;
  const actions = value.map((item, index) => {
    const type = clean(item["type"], "action");
    if (!["action", "transition", "dialogue", "inner_thought"].includes(type)) {
      invalid(`Action ${index + 1} has an unsupported type.`, [type]);
    }
    const content = clean(item["content"]);
    if (!content) invalid(`Action ${index + 1} has empty content.`);
    const duration = Math.max(400, Math.min(8_000, Math.round(
      Number(item["durationMs"]) || Number(item["weight"] || 1) * 1_200,
    )));
    const extensions = extensionMap(item["extensions"]);
    extensions["timestamp"] = formatTimestamp(cursor);
    extensions["duration_ms"] = duration;
    if (extensions["rewrite_duration_ms"] !== undefined) extensions["rewrite_duration_ms"] = duration;
    const base = {
      action_id: stableId("action", `${sceneSeed}:${index + 1}:${content}`, used) as ActionV4["action_id"],
      type: type as ActionV4["type"],
      content,
      extend: extensions,
    };
    cursor += duration;
    if (type === "action" || type === "transition") return base as ActionV4;
    const speakerName = clean(item["speaker"]);
    if (!speakerName) invalid(`Action ${index + 1} requires a speaker.`);
    const escapedSpeaker = speakerName.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
    if (new RegExp(`^${escapedSpeaker}\\s*[:：]`, "iu").test(content)) {
      invalid(`Action ${index + 1} repeats the speaker name in dialogue content.`);
    }
    if (/^(?:“[^”]+”|「[^」]+」|『[^』]+』|"[^"]+")$/u.test(content)) {
      invalid(`Action ${index + 1} wraps dialogue content in quotation marks.`);
    }
    const special = canonicalNameKey(speakerName);
    if (["旁白", "narrator"].includes(special)) {
      return { ...base, speaker: { kind: "broadcast", label: "旁白" }, ...(clean(item["parenthetical"]) ? { parenthetical: clean(item["parenthetical"]) } : {}) } as ActionV4;
    }
    if (["画外音", "offscreen"].includes(special)) {
      return { ...base, speaker: { kind: "offscreen", label: "画外音" }, ...(clean(item["parenthetical"]) ? { parenthetical: clean(item["parenthetical"]) } : {}) } as ActionV4;
    }
    const actor = resolveAsset(script, draft, "actors", speakerName, used, created) as ActorV4;
    const speakerVariant = clean(item["speakerVariant"]);
    const existing = actorRefs.find((ref) => ref.actor_id === actor.actor_id);
    let stateId: StateIdV4 | undefined;
    if (speakerVariant) {
      stateId = resolveState(actor, speakerVariant, draft, "actors", used, created);
      if (!existing) actorRefs.push({ actor_id: actor.actor_id, state_ids: [stateId] });
      else existing.state_ids = [...new Set([...existing.state_ids, stateId])].sort();
    } else if (!existing) {
      const defaultState = resolveState(actor, "默认", draft, "actors", used, created);
      actorRefs.push({ actor_id: actor.actor_id, state_ids: [defaultState] });
    }
    return {
      ...base,
      speaker: { actor_id: actor.actor_id, ...(stateId ? { state_id: stateId } : {}) },
      ...(clean(item["parenthetical"]) ? { parenthetical: clean(item["parenthetical"]) } : {}),
    } as ActionV4;
  });
  return { actions, actorRefs };
}

export function composeCreativeDraft(current: ScriptV4, rawDraft: unknown): CreativeDraftComposeResult {
  if (!isRecord(rawDraft) || rawDraft["version"] !== 1) invalid("Creative draft version must be 1.");
  if (!isRecord(rawDraft["episode"]) || !isRecord(rawDraft["scene"])) invalid("Creative draft requires episode and scene objects.");
  const draft = structuredClone(rawDraft);
  const episodeDraft = draft["episode"] as RecordValue;
  const sceneDraft = draft["scene"] as RecordValue;
  const script = structuredClone(current);
  const used = existingIds(script);
  const created: CreativeDraftComposeResult["created"] = { actors: [], locations: [], props: [], states: [] };
  const episodeNumber = positiveInteger(episodeDraft["number"], "episode.number");
  if (episodeNumber > script.episodes.length + 1) invalid("Episodes must be composed in order.", [episodeNumber]);
  let episode = script.episodes[episodeNumber - 1];
  if (!episode) {
    episode = {
      episode_id: stableId("episode", `episode:${episodeNumber}`, used) as EpisodeV4["episode_id"],
      title: clean(episodeDraft["title"], `第 ${episodeNumber} 集`),
      synopsis: clean(episodeDraft["synopsis"], `第 ${episodeNumber} 集剧情。`),
      scenes: [],
    };
    script.episodes.push(episode);
  } else {
    if (!clean(episode.title)) episode.title = clean(episodeDraft["title"], `第 ${episodeNumber} 集`);
    if (!clean(episode.synopsis)) episode.synopsis = clean(episodeDraft["synopsis"], `第 ${episodeNumber} 集剧情。`);
  }

  const location = resolveAsset(script, draft, "locations", sceneDraft["location"], used, created) as LocationV4;
  const locationState = resolveState(location, sceneDraft["locationVariant"], draft, "locations", used, created);
  const actorRefs: ActorRefV4[] = readableRefs(sceneDraft["actors"], "scene.actors").map((ref) => {
    const actor = resolveAsset(script, draft, "actors", ref["name"], used, created) as ActorV4;
    return { actor_id: actor.actor_id, state_ids: [resolveState(actor, ref["variant"], draft, "actors", used, created)] };
  });
  const actors = mergeStateRefs<ActorRefV4>([], actorRefs, (ref) => ref.actor_id);
  const propRefs: PropRefV4[] = readableRefs(sceneDraft["props"], "scene.props").map((ref) => {
    const prop = resolveAsset(script, draft, "props", ref["name"], used, created) as PropV4;
    return { prop_id: prop.prop_id, state_ids: [resolveState(prop, ref["variant"], draft, "props", used, created)] };
  });
  const props = mergeStateRefs<PropRefV4>([], propRefs, (ref) => ref.prop_id);
  const previous = episode.scenes.at(-1);
  const incomingBase: SceneV4 = {
    scene_id: stableId("scene", `episode:${episodeNumber}:scene:${episode.scenes.length + 1}:${clean(sceneDraft["title"])}`, used) as SceneV4["scene_id"],
    environment: { space: clean(sceneDraft["space"], "interior"), time: clean(sceneDraft["time"], "day") },
    location: { location_id: location.location_id, state_ids: [locationState] },
    actors,
    props,
    actions: [],
    extend: { scene_title: clean(sceneDraft["title"], `第 ${episode.scenes.length + 1} 场`) },
  };
  const boundary = clean(sceneDraft["boundary"], "auto");
  if (!["auto", "new", "continue"].includes(boundary)) invalid("scene.boundary must be auto, new, or continue.");
  const setupMatches = sameSetup(previous, incomingBase);
  if (boundary === "continue" && !setupMatches) invalid("scene.boundary=continue requires the same location, space, and time as the previous scene.");
  const joinedPreviousScene = boundary !== "new" && setupMatches;
  const targetScene = joinedPreviousScene && previous ? previous : incomingBase;
  const sceneNumber = joinedPreviousScene ? episode.scenes.length : episode.scenes.length + 1;
  const sceneActors = mergeStateRefs(targetScene.actors, incomingBase.actors, (ref) => ref.actor_id);
  const composed = draftActions(
    sceneDraft["actions"],
    script,
    draft,
    sceneActors,
    targetScene.scene_id,
    episodeCursor(episode),
    used,
    created,
  );
  targetScene.actors = composed.actorRefs;
  targetScene.props = mergeStateRefs(targetScene.props, incomingBase.props, (ref) => ref.prop_id);
  if (targetScene.location && incomingBase.location) {
    targetScene.location.state_ids = [...new Set([...targetScene.location.state_ids, ...incomingBase.location.state_ids])].sort();
  }
  targetScene.actions.push(...composed.actions);
  if (!joinedPreviousScene) episode.scenes.push(targetScene);

  const validation = validateScriptV4(script);
  if (!validation.ok) {
    invalid("Composed screenplay does not pass Script v4 validation.", validation.errors.slice(0, 12).map((issue) => `${issue.path}: ${issue.message}`));
  }
  return {
    script,
    validation,
    episodeNumber,
    sceneNumber,
    sceneId: targetScene.scene_id,
    actionIds: composed.actions.map((action) => action.action_id),
    joinedPreviousScene,
    created,
  };
}
